import { useEffect, useRef, useState } from 'react';
import type { EvaluationLine } from '../api/types';
import { DEFAULT_DEPTH, DEFAULT_MULTI_PV, probeEngineAsset, type EngineAvailability } from './config';
import { getSharedHub } from './analysis-hub';
import {
  EngineUnavailableError,
  type AnalysisSnapshot,
  type EngineWorkerFactory,
} from './local-engine';

/**
 * Continuous local analysis of whatever position the user is looking at.
 *
 * The engine is free per position once it is loaded, so analysis follows navigation
 * instead of waiting for a click: change `fen` and the previous search is cancelled
 * and replaced. Every caller goes through the shared `AnalysisHub`, so several
 * components watching the same position share one search instead of cancelling each
 * other. Results are advisory only and never leave the browser.
 */

export type LocalAnalysisStatus =
  | 'idle'          // switched off by the user
  | 'checking'      // probing for the vendored artifact
  | 'loading'       // first search; the WASM download/boot is in flight
  | 'analysing'
  | 'done'
  | 'missing'       // artifact not vendored in this deployment
  | 'unsupported'   // browser cannot run it
  | 'error';

export interface LocalAnalysisResult {
  status: LocalAnalysisStatus;
  lines: EvaluationLine[];
  depth: number;
  /** Set when the engine failed at runtime; `status` is then `'error'`. */
  error: string | null;
}

export interface UseLocalAnalysisOptions {
  depth?: number;
  multiPv?: number;
  /** Injectables for tests; production uses the real Worker and `fetch`. */
  workerFactory?: EngineWorkerFactory;
  probe?: () => Promise<EngineAvailability>;
}

/** True while the engine is on and able to run here (starting, searching or done). */
export function isLocalAnalysisActive(result: LocalAnalysisResult): boolean {
  return ['checking', 'loading', 'analysing', 'done'].includes(result.status);
}

const IDLE: LocalAnalysisResult = { status: 'idle', lines: [], depth: 0, error: null };

export function useLocalAnalysis(
  fen: string | null,
  enabled: boolean,
  options: UseLocalAnalysisOptions = {},
): LocalAnalysisResult {
  const { depth = DEFAULT_DEPTH, multiPv = DEFAULT_MULTI_PV, workerFactory, probe } = options;
  const [result, setResult] = useState<LocalAnalysisResult>(IDLE);

  // Availability is a property of the deployment and the browser, not of the
  // position, so it is probed once per mount and cached across navigation.
  const availability = useRef<EngineAvailability | null>(null);

  useEffect(() => {
    if (!enabled || fen === null) {
      setResult(IDLE);
      return undefined;
    }

    let cancelled = false;
    let unsubscribe: () => void = () => undefined;

    void (async () => {
      if (availability.current === null) {
        setResult({ status: 'checking', lines: [], depth: 0, error: null });
        availability.current = await (probe ? probe() : probeEngineAsset());
      }
      if (cancelled) return;

      const state = availability.current;
      if (state.kind === 'missing') {
        setResult({ status: 'missing', lines: [], depth: 0, error: null });
        return;
      }
      if (state.kind === 'unsupported') {
        setResult({ status: 'unsupported', lines: [], depth: 0, error: state.reason });
        return;
      }

      setResult({ status: 'loading', lines: [], depth: 0, error: null });
      unsubscribe = getSharedHub(workerFactory).subscribe({
        fen,
        depth,
        multiPv,
        onSnapshot: (snapshot: AnalysisSnapshot) => {
          // A snapshot for a position we have already navigated away from must not
          // overwrite the current one; the effect cleanup sets `cancelled` first.
          if (cancelled || snapshot.fen !== fen) return;
          setResult({
            status: snapshot.complete ? 'done' : 'analysing',
            // The shared search may be wider than this caller asked for.
            lines: snapshot.lines.filter((line) => line.rank <= multiPv),
            depth: snapshot.depth,
            error: null,
          });
        },
        onError: (cause: unknown) => {
          if (cancelled) return;
          const message =
            cause instanceof EngineUnavailableError
              ? cause.message
              : cause instanceof Error
                ? cause.message
                : 'the local engine stopped unexpectedly';
          setResult({ status: 'error', lines: [], depth: 0, error: message });
        },
      });
    })();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [fen, enabled, depth, multiPv, workerFactory, probe]);

  return result;
}
